import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Flex, FlexColumn } from '@elementary/common-ui/components';
import { Box, Collapse, Typography, useTheme } from '@mui/material';
import { radius, space } from '../../style/utils';
import { LINEAGE_COLOR_BY_LEGEND } from './constants';
import { useLineageGraphContext } from './LineageGraphContext';
import type { LineageNodeColors } from './types';
import {
  getFilterHighlightCss,
  getLineageCategoryColors,
} from './utils/lineageDisplaySettings';

const EXPAND_DELAY_MS = 150;

const Wrapper = styled(FlexColumn)`
  justify-content: center;
  background: ${({ theme }) => theme.palette.surface.base};
  padding: ${space('lg')};
  border-radius: ${radius('lg')};
  border: 1px solid ${({ theme }) => theme.palette.border.light};
  outline: none;
`;

const ColorByEntries = styled(FlexColumn)`
  gap: ${space('sm')};
  padding-bottom: ${space('md')};
  border-bottom: 1px solid ${({ theme }) => theme.palette.border.light};
`;

const LegendChip = styled(Flex)`
  align-items: center;
  gap: ${space('sm')};
`;

const ChipIcon = styled.div<{ $colors: LineageNodeColors }>`
  height: 10px;
  width: 10px;
  border-radius: ${radius('round')};
  border: 1px solid ${({ $colors }) => $colors.border};
  background: ${({ $colors }) => $colors.background};
`;

const FilteredChipIcon = styled.div`
  height: 10px;
  width: 10px;
  border-radius: ${radius('round')};
  border: 1px solid ${({ theme }) => theme.palette.border.medium};
  background: ${({ theme }) => theme.palette.surface.base};
  ${({ theme }) => getFilterHighlightCss(theme.palette, { scale: 0.5 })}
`;

type Props = {
  className?: string;
  /** Label for the highlighted/selected nodes legend entry. Defaults to "Filtered". */
  selectedLabel?: string;
  /** Shows only the selected entry until hovered or focused, for small embedded graphs. */
  isCompact?: boolean;
};

const Legend = ({
  className,
  selectedLabel = 'Filtered',
  isCompact = false,
}: Props) => {
  const theme = useTheme();
  const { displaySettings } = useLineageGraphContext();
  const colorByEntries = LINEAGE_COLOR_BY_LEGEND[displaySettings.colorBy];
  const [isExpanded, setIsExpanded] = useState(false);
  const expandTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const isOpen = !isCompact || isExpanded;

  useEffect(() => () => clearTimeout(expandTimeoutRef.current), []);

  const expand = (delayMs: number) => {
    clearTimeout(expandTimeoutRef.current);
    expandTimeoutRef.current = setTimeout(() => setIsExpanded(true), delayMs);
  };

  const collapse = () => {
    clearTimeout(expandTimeoutRef.current);
    setIsExpanded(false);
  };

  const expandHandlers = isCompact
    ? {
        role: 'group',
        'aria-label': 'Legend',
        tabIndex: 0,
        onMouseEnter: () => expand(EXPAND_DELAY_MS),
        onMouseLeave: collapse,
        onFocus: () => expand(0),
        onBlur: collapse,
      }
    : {};

  return (
    <Wrapper className={className} {...expandHandlers}>
      {colorByEntries.length > 0 && (
        <Collapse in={isOpen}>
          <Box
            sx={{
              pb: '12px',
              opacity: isOpen ? 1 : 0,
              transition: theme.transitions.create('opacity'),
            }}
          >
            <ColorByEntries>
              {colorByEntries.map(({ category, label }) => (
                <LegendChip key={category}>
                  <ChipIcon
                    $colors={getLineageCategoryColors(theme, category)}
                  />
                  <Typography>{label}</Typography>
                </LegendChip>
              ))}
            </ColorByEntries>
          </Box>
        </Collapse>
      )}
      <LegendChip>
        <FilteredChipIcon />
        <Typography>{selectedLabel}</Typography>
      </LegendChip>
    </Wrapper>
  );
};

export default Legend;
